import { queryClient } from "@api/queryClient";
import { useSessionKey } from "@auth/sessionStore";
import { AllDayTray } from "@calendar/AllDayTray";
import { calendarClient } from "@calendar/calendarClient";
import {
  calendarClock,
  calendarLayout,
  calendarDateKey,
  calendarTime,
  type DaySegment,
} from "@calendar/calendarLayout";
import { calendarKeys, useScheduledVisits } from "@calendar/calendarQueries";
import {
  groupCalendarVisits,
  summarizeCalendarGroup,
} from "@calendar/groupCalendarVisits";
import {
  formatDayNumber,
  formatWeekday,
  parseDateParam,
  rangeDays,
  shiftDays,
  toISODate,
} from "@calendar/weekDates";
import { usePatientSprite } from "@patients/signalment";
import { cn, PatientSprite } from "@scribenote/brian-design-system";
import { useOpenPatient } from "@shell/navigation";
import {
  isVisitCancelled,
  type StageStatus,
  type TodayVisit,
} from "@visits/visitsClient";
import { useEffect, useLayoutEffect, useRef, useState } from "react";

const MIN_ROW_PX = 64;
const MIN_BLOCK_PX = 24;
const GRID_PAD_PX = 10;
const DETAIL_MIN_BLOCK_PX = 52;
const WINDOW_MIN_HOUR = 8;
const WINDOW_MAX_HOUR = 18;

const stageEdge = (status: StageStatus | undefined): string => {
  switch (status) {
    case "active":
      return "border-l-ink-working";
    case "done":
      return "border-l-annotation";
    default:
      return "border-l-hollow";
  }
};

const CARD =
  "rounded-[4px] border border-l-[3px] border-rule bg-card shadow-[0_1px_0_var(--color-rule)]";

const firstName = (name: string | null): string | null =>
  name?.replace(/^dr\.?\s+/i, "").split(/\s+/)[0] || null;

const cardClock = (visit: TodayVisit, timeZone: string): string | null => {
  if (!visit.scheduledStart) return null;
  const start = calendarClock(visit.scheduledStart, timeZone);
  return visit.scheduledEnd
    ? `${start}–${calendarClock(visit.scheduledEnd, timeZone)}`
    : start;
};

interface PositionedVisit extends DaySegment {
  colIndex: number;
  colCount: number;
}

/** Chained overlaps form a cluster; each visit takes its lowest free column and all share the cluster's count. */
const layoutDay = (segments: DaySegment[]): PositionedVisit[] => {
  const items: PositionedVisit[] = segments
    .map((seg) => ({ ...seg, colIndex: 0, colCount: 1 }))
    .sort((a, b) => a.start - b.start || a.end - b.end);

  let cluster: PositionedVisit[] = [];
  let colEnds: number[] = [];
  const flush = () => {
    for (const it of cluster) it.colCount = colEnds.length;
    cluster = [];
    colEnds = [];
  };

  for (const it of items) {
    if (colEnds.length && it.start >= Math.max(...colEnds)) flush();
    const free = colEnds.findIndex((end) => end <= it.start);
    if (free === -1) {
      it.colIndex = colEnds.length;
      colEnds.push(it.end);
    } else {
      it.colIndex = free;
      colEnds[free] = it.end;
    }
    cluster.push(it);
  }
  flush();
  return items;
};

const fetchRange = (anchor: Date, dayCount: number): [string, string] => {
  const span = rangeDays(anchor, dayCount);
  return [toISODate(span[0]), toISODate(span[span.length - 1])];
};

export const WeekView = ({
  anchor,
  dayCount,
  timeZone,
}: {
  anchor: Date;
  dayCount: number;
  timeZone: string;
}) => {
  const sessionKey = useSessionKey();
  const openPatient = useOpenPatient();
  const openVisit = (visit: TodayVisit) => openPatient(visit.patientId);

  const days = rangeDays(anchor, dayCount);
  const [fetchStart, fetchEnd] = fetchRange(anchor, dayCount);
  const { data: visits } = useScheduledVisits(fetchStart, fetchEnd, sessionKey);

  // Prefetch neighbours so ◀ / ▶ animate over real content, not the skeleton.
  const prevAnchorKey = toISODate(shiftDays(anchor, -dayCount));
  const nextAnchorKey = toISODate(shiftDays(anchor, dayCount));
  useEffect(() => {
    for (const anchorKey of [prevAnchorKey, nextAnchorKey]) {
      const [start, end] = fetchRange(parseDateParam(anchorKey), dayCount);
      void queryClient.prefetchQuery({
        queryKey: calendarKeys.range(sessionKey, start, end),
        queryFn: () => calendarClient.range(start, end),
      });
    }
  }, [sessionKey, prevAnchorKey, nextAnchorKey, dayCount]);

  const scheduled = visits.filter((v) => v.scheduledStart);
  const unscheduled = visits.filter((v) => !v.scheduledStart);
  const scheduledGroups = groupCalendarVisits(scheduled);

  const { timed, stays } = calendarLayout(
    scheduledGroups,
    days.map(toISODate),
    timeZone,
  );
  const layoutByDay = timed.map(layoutDay);
  const todayKey = calendarDateKey(new Date(), timeZone);

  let windowStart = WINDOW_MIN_HOUR;
  let windowEnd = WINDOW_MAX_HOUR;
  for (const positioned of layoutByDay) {
    for (const p of positioned) {
      windowStart = Math.min(windowStart, Math.floor(p.start));
      windowEnd = Math.max(windowEnd, Math.ceil(p.end));
    }
  }
  windowStart = Math.max(0, windowStart);
  windowEnd = Math.min(24, Math.max(windowEnd, windowStart + 1));

  const rows = windowEnd - windowStart;

  const scrollRef = useRef<HTMLDivElement>(null);
  const [viewportHeight, setViewportHeight] = useState(0);
  const [scrollbarWidth, setScrollbarWidth] = useState(0);
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const measure = () => {
      setViewportHeight(el.clientHeight);
      setScrollbarWidth(el.offsetWidth - el.clientWidth);
    };
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    measure();
    return () => observer.disconnect();
  }, []);

  const rowPx = Math.max(
    MIN_ROW_PX,
    Math.floor((viewportHeight - 2 * GRID_PAD_PX) / rows),
  );
  const gridHeight = rows * rowPx;
  const contentHeight = gridHeight + 2 * GRID_PAD_PX;
  // Clip otherwise, so a stray sub-pixel can't produce a phantom scrollbar.
  const needsScroll = contentHeight > viewportHeight;

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col overflow-x-auto">
      <div
        className="flex min-h-0 flex-1 flex-col"
        style={{ minWidth: dayCount === 7 ? 1044 : undefined }}
      >
        <div className="flex shrink-0 overflow-y-hidden border-b border-rule bg-wall [scrollbar-gutter:stable]">
          <div className="w-14 shrink-0" />
          {days.map((day) => {
            const today = toISODate(day) === todayKey;
            return (
              <div
                key={day.toISOString()}
                aria-current={today ? "date" : undefined}
                className="flex min-w-0 flex-1 items-center justify-center gap-2 border-l border-rule py-[7px]"
              >
                <span className="font-mono text-fine-print uppercase tracking-[0.16em] text-annotation">
                  {formatWeekday(day)}
                </span>
                <span
                  className={cn(
                    "inline-flex h-[26px] min-w-[26px] items-center justify-center rounded-full px-1.5 font-display text-[0.9375rem] font-medium",
                    today ? "bg-ink text-ink-inverse" : "text-ink",
                  )}
                >
                  {formatDayNumber(day)}
                </span>
              </div>
            );
          })}
        </div>

        <AllDayTray
          scrollbarWidth={scrollbarWidth}
          stays={stays}
          dayCount={dayCount}
          timeZone={timeZone}
          onSelect={openVisit}
        />

        {unscheduled.length > 0 && (
          <div className="flex max-h-32 shrink-0 overflow-y-auto border-b border-rule bg-paper">
            <div className="flex w-14 shrink-0 items-center justify-end pr-2 font-mono text-fine-print text-annotation">
              no time
            </div>
            <div className="flex min-w-0 flex-1 flex-wrap gap-1.5 border-l border-rule p-1.5">
              {unscheduled.map((v) => {
                const cancelled = isVisitCancelled(v);
                return (
                  <button
                    key={v.id}
                    type="button"
                    onClick={() => openVisit(v)}
                    data-dd-privacy="hidden"
                    className={cn(
                      CARD,
                      stageEdge(v.currentStage?.status),
                      "flex min-w-0 max-w-64 cursor-pointer items-center gap-[7px] px-[7px] py-0.5 text-left",
                      "transition hover:-translate-y-px hover:shadow-lift-lg motion-reduce:transition-none",
                      "focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-accent",
                      cancelled && "opacity-60",
                    )}
                  >
                    <VisitSprite patientId={v.patientId} />
                    <span className="flex min-w-0 flex-col leading-[1.15]">
                      <span
                        className={cn(
                          "truncate text-[13px] font-semibold text-ink",
                          cancelled && "line-through",
                        )}
                      >
                        {v.patientName}
                      </span>
                      <span className="truncate font-mono text-[10px] text-annotation">
                        {[
                          v.reason ?? v.visitType,
                          firstName(v.assignedUserName),
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div
          ref={scrollRef}
          className={`relative min-h-0 flex-1 [scrollbar-gutter:stable] ${needsScroll ? "overflow-y-auto" : "overflow-y-hidden"}`}
          data-testid="calendar-grid"
        >
          {layoutByDay.every((items) => items.length === 0) &&
            unscheduled.length === 0 &&
            stays.length === 0 && (
              <p className="pointer-events-none absolute inset-x-14 top-10 z-10 text-center text-sm text-annotation">
                Nothing booked.
              </p>
            )}
          <div className="flex" style={{ height: contentHeight }}>
            <div className="relative w-14 shrink-0">
              {Array.from({ length: rows }).map((_, i) => (
                <div
                  key={i}
                  style={{ top: GRID_PAD_PX + i * rowPx }}
                  className="absolute right-2 -translate-y-1/2 font-mono text-fine-print tabular-nums text-annotation"
                >
                  {formatHourLabel(windowStart + i)}
                </div>
              ))}
            </div>

            {days.map((day, dayIndex) => {
              const positioned = layoutByDay[dayIndex];
              return (
                <div
                  key={day.toISOString()}
                  className="relative min-w-0 flex-1 border-l border-rule"
                >
                  {toISODate(day) === todayKey && (
                    <div className="pointer-events-none absolute inset-0 bg-paper/80" />
                  )}
                  <div
                    className="pointer-events-none absolute inset-x-0 grid"
                    style={{
                      top: GRID_PAD_PX,
                      height: gridHeight,
                      gridTemplateRows: `repeat(${rows}, 1fr)`,
                    }}
                  >
                    {Array.from({ length: rows }).map((_, i) => (
                      <div
                        key={i}
                        className={cn(
                          "border-t border-rule",
                          i === rows - 1 && "border-b",
                        )}
                      />
                    ))}
                  </div>
                  {positioned.map((p) => (
                    <VisitBlock
                      key={p.group.visits.map(({ id }) => id).join("-")}
                      item={p}
                      windowStart={windowStart}
                      rowPx={rowPx}
                      offsetPx={GRID_PAD_PX}
                      timeZone={timeZone}
                      onSelect={openVisit}
                    />
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

const VisitSprite = ({ patientId }: { patientId: string }) => {
  const { data } = usePatientSprite(patientId);
  return <PatientSprite size={20} sprite={data} />;
};

const VisitBlock = ({
  item,
  windowStart,
  rowPx,
  offsetPx,
  timeZone,
  onSelect,
}: {
  item: PositionedVisit;
  windowStart: number;
  rowPx: number;
  offsetPx: number;
  timeZone: string;
  onSelect: (visit: TodayVisit) => void;
}) => {
  const {
    group,
    start,
    end,
    colIndex,
    colCount,
    continuesBefore,
    continuesAfter,
  } = item;
  // Known limitation: a multi-patient block opens only its first visit.
  const visit = group.visits[0];
  const { isMultiPatient, cancelled, accentStatus, title, subtitle } =
    summarizeCalendarGroup(group);
  const detail = isMultiPatient ? subtitle : (visit.reason ?? visit.visitType);
  // A visit crossing the day boundary bleeds into the padding with a square corner.
  const top =
    offsetPx + (start - windowStart) * rowPx - (continuesBefore ? offsetPx : 0);
  const rawHeight = (end - start) * rowPx;
  const height =
    Math.max(rawHeight - 2, MIN_BLOCK_PX) +
    (continuesBefore ? offsetPx : 0) +
    (continuesAfter ? offsetPx : 0);
  const widthPct = 100 / colCount;

  const name =
    title ?? [visit.patientName, visit.clientName].filter(Boolean).join(" – ");
  const range = `${calendarTime(visit.scheduledStart as string, timeZone)}${visit.scheduledEnd ? ` – ${calendarTime(visit.scheduledEnd, timeZone)}` : ""}`;
  const clock = continuesBefore
    ? "continued"
    : `${cardClock(visit, timeZone)}${continuesAfter ? " · continues" : ""}`;
  const provider = firstName(visit.assignedUserName);

  return (
    <button
      type="button"
      onClick={() => onSelect(visit)}
      aria-label={`${name}, ${range}${cancelled ? ", cancelled" : ""}`}
      title={`${name}${detail ? ` · ${detail}` : ""}\n${range}`}
      data-dd-privacy="hidden"
      style={{
        top,
        height,
        left: `calc(${colIndex * widthPct}% + 3px)`,
        width: `calc(${widthPct}% - 6px)`,
      }}
      className={cn(
        CARD,
        stageEdge(accentStatus),
        "@container absolute flex cursor-pointer gap-[7px] overflow-hidden px-[7px] py-0.5 text-left",
        height < DETAIL_MIN_BLOCK_PX ? "items-center" : "items-start pt-1.5",
        "transition hover:z-10 hover:-translate-y-px hover:shadow-lift-lg motion-reduce:transition-none",
        "focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-accent",
        continuesBefore && "rounded-t-none border-t-0",
        continuesAfter && "rounded-b-none border-b-0",
        cancelled && "opacity-60",
      )}
    >
      {!isMultiPatient && (
        <span className="hidden shrink-0 @[120px]:inline-flex">
          <VisitSprite patientId={visit.patientId} />
        </span>
      )}
      <span className="flex min-w-0 flex-col leading-[1.15]">
        <span
          className={cn(
            "truncate text-[13px] font-semibold text-ink",
            cancelled && "line-through",
          )}
        >
          {title ?? visit.patientName}
        </span>
        <span className="truncate font-mono text-[10px] text-annotation">
          {clock}
          {provider && !isMultiPatient && ` · ${provider}`}
        </span>
        {height >= DETAIL_MIN_BLOCK_PX && detail && (
          <span className="mt-0.5 truncate text-xs text-annotation">
            {detail}
          </span>
        )}
      </span>
    </button>
  );
};

const formatHourLabel = (hour: number): string => {
  const h = ((hour + 11) % 12) + 1;
  return `${h}${hour < 12 || hour === 24 ? "am" : "pm"}`;
};
